/**
 * Payload shapes for the plotter analytics published over MQTT.
 *
 * Two reports are published (see `Topics.AnalyticsProfile` / `Topics.AnalyticsUsage`):
 *
 *  - A one-off device profile describing the plotter, its screen and which web
 *    platform features it supports. Published retained so the embedded device
 *    always has the latest profile for the unit.
 *  - A periodic usage report with in-memory counters accumulated since the
 *    previous report: screens and widgets viewed, control presses, and errors.
 *    Nothing is persisted in the browser, so a session that stops reporting
 *    without `final: true` means the client reloaded or crashed.
 */

export enum ViewKind {
  Screen = "screen",
  Widget = "widget",
}

export enum ErrorKind {
  Error = "error",
  UnhandledRejection = "unhandled_rejection",
  Resource = "resource",
  Reported = "reported",
}

export interface ViewUsage {
  /** How many times the view was entered during the reporting window */
  views: number;
  /** Accumulated time the view was mounted, in seconds */
  seconds: number;
}

export interface ErrorReport {
  kind: ErrorKind;
  message: string;
  source: string | null;
  line: number | null;
  column: number | null;
  stack: string | null;
  /** Identical errors are collapsed into one entry with a count */
  count: number;
  first_timestamp: string;
  last_timestamp: string;
}

export interface ClientHintBrand {
  brand: string;
  version: string;
}

/**
 * `navigator.userAgentData`. Requires a secure context and Chromium 90+, so on
 * the units (plain HTTP from the device) this is expected to be unavailable.
 */
export interface ClientHints {
  available: boolean;
  brands: ClientHintBrand[] | null;
  mobile: boolean | null;
  platform: string | null;
  architecture: string | null;
  bitness: string | null;
  /** Hardware model, the one high-entropy hint the UA string often omits */
  model: string | null;
  platform_version: string | null;
  ua_full_version: string | null;
  full_version_list: ClientHintBrand[] | null;
}

/**
 * Chromium version bracketed from observed CSS/DOM feature support, independent
 * of the user agent string. Both bounds can be null when nothing is conclusive.
 */
export interface VersionEstimate {
  /** The engine is at least this new, because this feature is present */
  min_version: number | null;
  min_version_feature: string | null;
  /** The engine is older than this, because this feature is absent */
  max_version: number | null;
  max_version_feature: string | null;
}

export interface BrowserInfo {
  /** Best-effort engine name derived from the user agent, e.g. "chrome" */
  name: string | null;
  version: string | null;
  major_version: number | null;
  /** Chromium/Blink version, which is what compatibility decisions hinge on */
  chromium_version: string | null;
  chromium_major_version: number | null;
  /** Where the Chromium version came from; client hints outrank the UA string */
  chromium_version_source: "client_hints" | "user_agent" | null;
  /**
   * Engine detection that does not read the user agent at all, for plotters that
   * rewrite or omit it. `is_v8` is the strongest single signal.
   */
  is_chromium: boolean;
  is_v8: boolean;
  is_blink: boolean;
  webkit_version: string | null;
  /** True when the UA identifies an embedded WebView rather than a browser */
  is_webview: boolean;
  qt_webengine_version: string | null;
  /** Feature-derived version bracket, to sanity-check the reported version */
  version_estimate: VersionEstimate;
}

export interface DeviceInfo {
  user_agent: string | null;
  /** False when the UA is missing or carries no engine token we can act on */
  user_agent_reliable: boolean;
  browser: BrowserInfo;
  /** null until the asynchronous lookup resolves; see `ClientHints` */
  client_hints: ClientHints | null;
  platform: string | null;
  vendor: string | null;
  os: string | null;
  os_version: string | null;
  /** Model string when the UA exposes one (typical for Android WebViews) */
  hardware_model: string | null;
  /** Marine vendor token found in the UA, e.g. "garmin", "raymarine" */
  plotter_vendor_hint: string | null;
  hardware_concurrency: number | null;
  device_memory_gb: number | null;
  max_touch_points: number | null;
  touch_events: boolean;
  pointer_events: boolean;
  language: string | null;
  languages: string[] | null;
  timezone: string | null;
  online: boolean | null;
}

export interface DisplayInfo {
  screen_width: number | null;
  screen_height: number | null;
  avail_width: number | null;
  avail_height: number | null;
  viewport_width: number | null;
  viewport_height: number | null;
  document_width: number | null;
  document_height: number | null;
  device_pixel_ratio: number | null;
  /** screen size multiplied by the pixel ratio, i.e. physical pixels */
  effective_width: number | null;
  effective_height: number | null;
  color_depth: number | null;
  orientation: string | null;
}

/**
 * Feature probes. `null` means the probe itself could not run on this client,
 * which is a meaningful answer distinct from "not supported".
 */
export interface CssCapabilities {
  grid: boolean | null;
  /** `fr` units in a grid template, which the layout leans on heavily */
  grid_template_fr: boolean | null;
  flexbox: boolean | null;
  flex_gap: boolean | null;
  gap_property: boolean | null;
  is_selector: boolean;
  where_selector: boolean;
  has_selector: boolean;
  not_selector_list: boolean;
  custom_properties: boolean | null;
  calc: boolean | null;
  min_max_clamp: boolean | null;
  aspect_ratio: boolean | null;
  position_sticky: boolean | null;
  transform_3d: boolean | null;
  transitions: boolean | null;
  backdrop_filter: boolean | null;
  clip_path: boolean | null;
  conic_gradient: boolean | null;
  container_queries: boolean | null;
  logical_properties: boolean | null;
  object_fit: boolean | null;
  overscroll_behavior: boolean | null;
  supports_api: boolean;
}

/**
 * ES built-in probes report the *engine's own* implementation. `core-js` is
 * imported at startup and backfills these on old engines, so merely finding the
 * function would prove nothing about the browser — each one is checked for a
 * native implementation instead.
 */
export interface JsCapabilities {
  /** True when a built-in exists but is a polyfill rather than the engine's */
  builtins_polyfilled: boolean;
  promise: boolean;
  symbol: boolean;
  proxy: boolean;
  weak_map: boolean;
  map_set: boolean;
  object_assign: boolean;
  object_entries: boolean;
  object_from_entries: boolean;
  array_includes: boolean;
  array_flat: boolean;
  string_replace_all: boolean;
  fetch: boolean;
  abort_controller: boolean;
  intl: boolean;
  intl_relative_time_format: boolean;
  resize_observer: boolean;
  mutation_observer: boolean;
  intersection_observer: boolean;
  request_animation_frame: boolean;
  performance_now: boolean;
  performance_observer: boolean;
  performance_memory: boolean;
  web_workers: boolean;
  service_worker: boolean;
  custom_elements: boolean;
  shadow_dom: boolean;
  /** Reported only; analytics never reads or writes web storage */
  local_storage_available: boolean;
  session_storage_available: boolean;
  /**
   * Syntax-level probes, evaluated through `new Function`. `null` when
   * evaluation is blocked (e.g. by a Content-Security-Policy).
   */
  es6_syntax: boolean | null;
  async_await: boolean | null;
  optional_chaining: boolean | null;
  nullish_coalescing: boolean | null;
}

export interface WebSocketCapabilities {
  supported: boolean;
  binary_type: boolean;
  array_buffer: boolean;
  blob: boolean;
  close_event: boolean;
  /** Protocol of the page itself, "http:" or "https:" */
  page_protocol: string | null;
  /** Protocol actually used for the live MQTT connection, "ws" or "wss" */
  broker_protocol: string | null;
  broker_host: string | null;
  broker_port: number | null;
  broker_path: string | null;
  secure: boolean | null;
  mqtt_protocol_version: number | null;
  mqtt_keepalive_seconds: number | null;
  connected: boolean | null;
}

export interface RenderingCapabilities {
  canvas_2d: boolean;
  canvas_filter: boolean;
  webgl: boolean;
  webgl2: boolean;
  webgl_vendor: string | null;
  webgl_renderer: string | null;
  /**
   * Heuristic from the unmasked WebGL renderer string: false when it names a
   * known software rasteriser (SwiftShader, llvmpipe, Mesa offscreen, ...).
   */
  hardware_accelerated: boolean | null;
  max_texture_size: number | null;
  svg: boolean;
  /** Ops/ms from the existing browser speed benchmark, for device tiering */
  browser_speed: number | null;
}

export interface Capabilities {
  css: CssCapabilities;
  js: JsCapabilities;
  websocket: WebSocketCapabilities;
  rendering: RenderingCapabilities;
}

export interface DeviceProfileReport {
  type: "profile";
  session_id: string;
  /** The embedded box this plotter is connected to. Shared by every plotter on the vessel. */
  device_id: string | null;
  /** The vessel this plotter belongs to, from the backend config. */
  vessel_id: string | null;
  software_version: string;
  timestamp: string;
  session_start_timestamp: string;
  device: DeviceInfo;
  display: DisplayInfo;
  capabilities: Capabilities;
}

export interface UsageReport {
  type: "usage";
  session_id: string;
  /** The embedded box this plotter is connected to. Shared by every plotter on the vessel. */
  device_id: string | null;
  /** The vessel this plotter belongs to, from the backend config. */
  vessel_id: string | null;
  software_version: string;
  /** Increments per report, so the device can spot gaps */
  sequence: number;
  /** True on the report published while the page is unloading */
  final: boolean;
  timestamp: string;
  session_start_timestamp: string;
  session_duration_seconds: number;
  /** Length of the window these counters cover, in seconds */
  window_seconds: number;
  trip_id: string | null;
  screens: Record<string, ViewUsage>;
  widgets: Record<string, ViewUsage>;
  /**
   * Press counts per UI control, keyed by the control's `data-analytics` name or
   * `aria-label`. Captured automatically by the press tracker.
   */
  presses: Record<string, number>;
  /** Counts for events reported explicitly through `trackEvent` */
  events: Record<string, number>;
  errors: ErrorReport[];
  /** Errors discarded because the in-memory buffer was full */
  dropped_errors: number;
}
